import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { getUserHomes } from '../screens/home/api';
import type { HomeResponse } from '../client/types.gen';
import { useAuth } from './AuthContext';
import { useParams } from 'react-router-dom';

interface HomesContextType {
  homes: HomeResponse[];
  loading: boolean;
  error: Error | null;
  refreshHomes: () => Promise<HomeResponse[]>;
}

const HomesContext = createContext<HomesContextType | undefined>(undefined);

export function HomesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [homes, setHomes] = useState<HomeResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadHomes = useCallback(async () => {
    if (!user) {
      setHomes([]);
      setLoading(false);
      return [];
    }

    try {
      setLoading(true);
      setError(null);
      const userHomes = await getUserHomes();
      setHomes(userHomes);

      return userHomes;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load homes'));
      setHomes([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadHomes();
  }, [loadHomes]);

  const refreshHomes = useCallback(async () => {
    return await loadHomes();
  }, [loadHomes]);

  const value = {
    homes,
    loading,
    error,
    refreshHomes,
  };

  return <HomesContext.Provider value={value}>{children}</HomesContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useHomes() {
  const context = useContext(HomesContext);
  if (context === undefined) {
    throw new Error('useHomes must be used within a HomesProvider');
  }
  return context;
}

/**
 * The home for the current route.
 *
 * `HomesProvider` is mounted above `<RouterProvider>` in main.tsx (so the
 * homes list survives navigation) — a `useParams()` call made there would
 * render outside the router's matched-route tree and never see the URL's
 * `:homeId`. This hook reads `:homeId` from its own call site instead, which
 * works because every caller is a screen actually rendered under
 * `/home/:homeId/...`.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCurrentHome() {
  const { homes, loading, error, refreshHomes } = useHomes();
  const { homeId } = useParams<{ homeId: string }>();
  const currentHome = homes.find(h => h.id === homeId) ?? null;
  const members = currentHome?.members ?? [];

  return { currentHome, members, loading, error, refreshHomes, homes };
}
